import { Controller } from "@hotwired/stimulus"

const FREQUENCIES = ["normal", "quiet", "off"]

export default class extends Controller {
  static targets = ["frequencyLabel"]
  static values = {
    messageId: Number,
    priority: { type: String, default: "low" },
    state: { type: String, default: "wave" },
    normalLabel: String,
    quietLabel: String,
    offLabel: String
  }

  connect() {
    this.frequency = this.readFrequency()
    this.updateFrequencyLabel()

    if (this.shouldSuppress() || this.isDismissed()) {
      this.removeImmediately()
      return
    }

    requestAnimationFrame(() => {
      this.element.classList.add("is-visible")
    })

    this.applyState()
  }

  dismiss(event) {
    event?.preventDefault()
    this.rememberDismissed()
    this.hideAndRemove()
  }

  cycleFrequency(event) {
    event?.preventDefault()

    const index = FREQUENCIES.indexOf(this.frequency)
    const next = FREQUENCIES[(index + 1) % FREQUENCIES.length]

    this.frequency = next
    this.writeFrequency(next)
    this.updateFrequencyLabel()

    if (this.shouldSuppress()) {
      this.hideAndRemove()
    }
  }

  applyState() {
    this.element.classList.remove("is-wave", "is-celebrate")

    if (this.stateValue === "celebrate") {
      this.element.classList.add("is-celebrate")
      return
    }

    this.element.classList.add("is-wave")
  }

  hideAndRemove() {
    this.element.classList.remove("is-visible")
    window.setTimeout(() => this.removeImmediately(), 200)
  }

  removeImmediately() {
    this.element.remove()
  }

  isDismissed() {
    if (!this.messageIdValue) return false

    try {
      return window.localStorage.getItem(this.dismissalKey) === "1"
    } catch (_error) {
      return false
    }
  }

  rememberDismissed() {
    if (!this.messageIdValue) return

    try {
      window.localStorage.setItem(this.dismissalKey, "1")
    } catch (_error) {
      // Ignore storage failures in private mode.
    }
  }

  shouldSuppress() {
    if (this.frequency === "off") return true
    if (this.frequency !== "quiet") return false

    return this.priorityValue === "low"
  }

  readFrequency() {
    try {
      const value = window.localStorage.getItem("viddy:frequency")
      if (FREQUENCIES.includes(value)) return value
    } catch (_error) {
      // Ignore storage failures in private mode.
    }

    return "normal"
  }

  writeFrequency(value) {
    try {
      window.localStorage.setItem("viddy:frequency", value)
    } catch (_error) {
      // Ignore storage failures in private mode.
    }
  }

  updateFrequencyLabel() {
    if (!this.hasFrequencyLabelTarget) return

    const labels = {
      normal: this.normalLabelValue || "Viddy: Normal",
      quiet: this.quietLabelValue || "Viddy: Quiet",
      off: this.offLabelValue || "Viddy: Off"
    }

    this.frequencyLabelTarget.textContent = labels[this.frequency] || labels.normal
  }

  get dismissalKey() {
    return `viddy:dismissed:${this.messageIdValue}`
  }
}
